import { useState, useCallback, useRef, useMemo } from 'react';
import type { CardState, CardFormData, LanguageCode } from '../types/entryRules.types';
import {
  getEntryRules,
  createEntryRule,
  updateEntryRule,
  deleteEntryRule,
  EntryRuleConflictError,
} from '../api/entryRules';
import { sanitizeText, isCardDirty, getUsedCountryCodes } from '../utils/entryRules.utils';
import { toast } from 'sonner';

function makeEmptyCard(): CardState {
  return {
    localId: crypto.randomUUID(),
    serverId: null,
    status: 'new',
    isExpanded: true,
    isSaving: false,
    savedData: null,
    currentData: { countryCode: '', ruleText: '' },
  };
}

export function useEntryRules() {
  const [cards, setCards] = useState<CardState[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Keeps async callbacks (saveCard/deleteCard) reading the latest cards without
  // depending on `cards`, so their identity stays stable across keystrokes and
  // the memoized EntryRuleCard isn't re-rendered for every card on each edit.
  const cardsRef = useRef(cards);
  cardsRef.current = cards;

  const loadRules = useCallback(async (lang: LanguageCode) => {
    setIsLoading(true);
    try {
      const rules = await getEntryRules(lang);
      setCards(
        rules.map((r) => ({
          localId: crypto.randomUUID(),
          serverId: r.id,
          status: 'saved' as const,
          isExpanded: true,
          isSaving: false,
          savedData: { countryCode: r.countryCode, ruleText: r.ruleText },
          currentData: { countryCode: r.countryCode, ruleText: r.ruleText },
        }))
      );
    } catch {
      toast.error('Не удалось загрузить правила въезда');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const addCard = useCallback((): string => {
    const card = makeEmptyCard();
    setCards((prev) => [...prev, card]);
    return card.localId;
  }, []);

  const updateCardData = useCallback(
    (localId: string, field: keyof CardFormData, value: string) => {
      setCards((prev) =>
        prev.map((card) => {
          if (card.localId !== localId) return card;
          const newData = { ...card.currentData, [field]: value };
          const dirty = isCardDirty(newData, card.savedData);
          return {
            ...card,
            currentData: newData,
            status: card.status === 'new' ? 'new' : dirty ? 'dirty' : 'saved',
          };
        })
      );
    },
    []
  );

  const saveCard = useCallback(async (localId: string, lang: LanguageCode) => {
    const card = cardsRef.current.find((c) => c.localId === localId);
    if (!card) return;

    const duplicate = cardsRef.current.some(
      (c) =>
        c.localId !== localId &&
        c.currentData.countryCode === card.currentData.countryCode
    );
    if (duplicate) {
      toast.error('Правило въезда для этой страны уже существует');
      return;
    }

    const sanitized = sanitizeText(card.currentData.ruleText);
    const dataToSave: CardFormData = {
      countryCode: card.currentData.countryCode,
      ruleText: sanitized,
    };

    setCards((prev) =>
      prev.map((c) => (c.localId === localId ? { ...c, isSaving: true } : c))
    );

    try {
      if (card.serverId) {
        const updated = await updateEntryRule(card.serverId, lang, dataToSave);
        setCards((prev) =>
          prev.map((c) => (c.localId === localId ? { ...c, serverId: updated.id } : c))
        );
        toast.success('Правило въезда обновлено');
      } else {
        const created = await createEntryRule({ ...dataToSave, language: lang });
        setCards((prev) =>
          prev.map((c) =>
            c.localId === localId
              ? {
                  ...c,
                  serverId: created.id,
                  status: 'saved',
                  isSaving: false,
                  savedData: dataToSave,
                  currentData: dataToSave,
                }
              : c
          )
        );
        toast.success('Правило въезда создано');
        return;
      }

      setCards((prev) =>
        prev.map((c) =>
          c.localId === localId
            ? {
                ...c,
                status: 'saved',
                isSaving: false,
                savedData: dataToSave,
                currentData: dataToSave,
              }
            : c
        )
      );
    } catch (err) {
      toast.error(
        err instanceof EntryRuleConflictError
          ? 'Правило въезда для этой страны уже существует'
          : 'Не удалось сохранить правило въезда'
      );
      setCards((prev) =>
        prev.map((c) => (c.localId === localId ? { ...c, isSaving: false } : c))
      );
    }
  }, []);

  const deleteCard = useCallback(async (localId: string, lang: LanguageCode) => {
    const card = cardsRef.current.find((c) => c.localId === localId);
    if (!card) return;

    if (card.serverId) {
      await deleteEntryRule(card.serverId, lang);
    }
    setCards((prev) => prev.filter((c) => c.localId !== localId));
  }, []);

  const toggleExpand = useCallback((localId: string) => {
    setCards((prev) =>
      prev.map((c) => (c.localId === localId ? { ...c, isExpanded: !c.isExpanded } : c))
    );
  }, []);

  // Keyed on the chosen countries alone so that typing in a rule's text field does
  // not hand every card a fresh Set and re-render the whole memoized list.
  const usedCodesKey = cards
    .map((c) => c.currentData.countryCode.trim())
    .sort()
    .join('|');
  const usedCountryCodes = useMemo(
    () => getUsedCountryCodes(cardsRef.current),
    [usedCodesKey]
  );

  const hasUnsavedCards = cards.some((c) => c.status !== 'saved');
  const hasNewCard = cards.some((c) => c.status === 'new');
  const canAddRule = !hasNewCard;

  return {
    cards,
    usedCountryCodes,
    isLoading,
    loadRules,
    addCard,
    updateCardData,
    saveCard,
    deleteCard,
    toggleExpand,
    hasUnsavedCards,
    hasNewCard,
    canAddRule,
  };
}
